import type { CollectionFieldPathFlattenedItem, CollectionFieldPathItem, CollectionFieldPathItemKind } from '#layers/data-model/utils/collection-field-path';
import type { FlattenedItem, NestedSelectMenuItem } from '#layers/ui/public';
import Fuse from 'fuse.js';
import { prioritizeTopLevelMatches } from '#layers/ui/public';

/*
 * Query matching and ranking for field paths. A query is split into tokens that must match the
 * path's display labels or API names in order: strictly by prefix when any path matches that way,
 * otherwise fuzzily. Matching paths are then ranked with Fuse, preferring root-level fields.
 */

interface CollectionFieldPathSearchEntry {
	displayPathLabels: string[];
	apiPathLabels: string[];
	leafDisplayLabel: string;
	leafApiName: string;
	depth: number;
}

/**
 * Per-token fuzzy matching. The collection field-path graph prunes with the same options, so it
 * can only admit a superset of the paths matchesOrderedFuzzyTokens accepts.
 */
export const FUZZY_TOKEN_OPTIONS = {
	ignoreLocation: true,
	threshold: 0.3,
	keys: ['token'],
};

export function normalizeCollectionFieldPathTokenSource(value: string): string[] {
	return value
		.replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
		.toLowerCase()
		.split(/[\s.]+/)
		.filter(Boolean);
}

export function normalizeCollectionFieldPathQuery(query: string): string[] {
	return normalizeCollectionFieldPathTokenSource(query.trim());
}

function matchesOrderedTokenPrefixes(queryTokens: string[], candidateTokens: string[]): boolean {
	let candidateIndex = 0;

	for (const queryToken of queryTokens) {
		let matched = false;

		while (candidateIndex < candidateTokens.length) {
			if (candidateTokens[candidateIndex]!.startsWith(queryToken)) {
				matched = true;
				candidateIndex++;
				break;
			}

			candidateIndex++;
		}

		if (!matched) return false;
	}

	return true;
}

function isCollectionFieldPathFlattenedItem(item: FlattenedItem): item is CollectionFieldPathFlattenedItem {
	return item.path.every((segment) => isCollectionFieldPathItem(segment));
}

function isCollectionFieldPathItem(item: NestedSelectMenuItem): item is CollectionFieldPathItem {
	return typeof (item as Partial<CollectionFieldPathItem>).apiName === 'string'
		&& typeof (item as Partial<CollectionFieldPathItem>).displayName === 'string'
		&& isCollectionFieldPathItemKind((item as Partial<CollectionFieldPathItem>).fieldPathKind);
}

function isCollectionFieldPathItemKind(value: unknown): value is CollectionFieldPathItemKind {
	return value === 'primitive' || value === 'single-relation' || value === 'list-relation';
}

export function matchesOrderedFuzzyTokens(queryTokens: string[], candidateTokens: string[]): boolean {
	let candidateIndex = 0;

	for (const queryToken of queryTokens) {
		const searchableTokens = candidateTokens
			.slice(candidateIndex)
			.map((token, index) => ({ token, index: candidateIndex + index }));

		if (searchableTokens.length === 0) return false;

		const match = new Fuse(searchableTokens, FUZZY_TOKEN_OPTIONS).search(queryToken)[0];

		if (!match) return false;

		candidateIndex = match.item.index + 1;
	}

	return true;
}

export function searchCollectionFieldPathEntries<T>(
	items: T[],
	query: string,
	getEntry: (item: T) => CollectionFieldPathSearchEntry,
): T[] {
	const queryTokens = normalizeCollectionFieldPathQuery(query);

	if (queryTokens.length === 0) return items;

	const candidates = items.map((item) => {
		const entry = getEntry(item);

		return {
			item,
			entry,
			displayTokens: entry.displayPathLabels.flatMap((label) => normalizeCollectionFieldPathTokenSource(label)),
			apiTokens: entry.apiPathLabels.flatMap((label) => normalizeCollectionFieldPathTokenSource(label)),
		};
	});

	const matchesStrictQuery = (candidate: typeof candidates[number]) =>
		matchesOrderedTokenPrefixes(queryTokens, candidate.displayTokens)
		|| matchesOrderedTokenPrefixes(queryTokens, candidate.apiTokens);

	const matchesFuzzyQuery = (candidate: typeof candidates[number]) =>
		matchesOrderedFuzzyTokens(queryTokens, candidate.displayTokens)
		|| matchesOrderedFuzzyTokens(queryTokens, candidate.apiTokens);

	const strictMatches = candidates.filter((candidate) => matchesStrictQuery(candidate));
	const fuzzyMatches = strictMatches.length === 0
		? candidates.filter((candidate) => matchesFuzzyQuery(candidate))
		: [];
	const rankingPool = strictMatches.length > 0 ? strictMatches : fuzzyMatches;

	if (rankingPool.length === 0) return [];

	const normalizedQuery = queryTokens.join(' ');

	const ranked = new Fuse(
		rankingPool.map((candidate) => ({
			item: candidate.item,
			leafDisplayLabel: candidate.entry.leafDisplayLabel,
			leafApiName: candidate.entry.leafApiName,
			displayPathText: candidate.entry.displayPathLabels.join(' '),
			apiPathText: candidate.entry.apiPathLabels.join(' '),
		})),
		{
			ignoreLocation: true,
			threshold: strictMatches.length > 0 ? 0.4 : 0.3,
			keys: ['leafDisplayLabel', 'leafApiName', 'displayPathText', 'apiPathText'],
		},
	).search(normalizedQuery).map((result) => result.item.item);

	const orderedResults = ranked.length > 0 ? ranked : rankingPool.map((candidate) => candidate.item);

	return prioritizeTopLevelMatches(orderedResults, (item) => getEntry(item).depth === 1);
}

export function searchCollectionFieldPathItems(items: FlattenedItem[], query: string): FlattenedItem[] {
	const candidates = items.filter((item) => isCollectionFieldPathFlattenedItem(item));

	return searchCollectionFieldPathEntries(candidates, query, (item) => ({
		displayPathLabels: item.path.map((segment) => segment.displayName),
		apiPathLabels: item.path.map((segment) => segment.apiName),
		leafDisplayLabel: item.item.displayName,
		leafApiName: item.item.apiName,
		depth: item.path.length,
	}));
}
